import type { LeadStageHistoryEntry } from '@utazzo/shared';

type StatusLike = { name: string };
type SubStatusLike = { name: string } | null | undefined;

/** Human label for a parent status plus optional query sub-status. */
export function formatStatusWithSubStatus(status: StatusLike, subStatus: SubStatusLike): string {
  return subStatus ? `${status.name} · ${subStatus.name}` : status.name;
}

/** Activity-timeline title for a stage-history row. */
export function formatStageHistoryTransition(entry: LeadStageHistoryEntry): string {
  const to = formatStatusWithSubStatus(entry.toStatus, entry.toSubStatus);
  if (!entry.fromStatus) {
    return `Moved to ${to}`;
  }
  const from = formatStatusWithSubStatus(entry.fromStatus, entry.fromSubStatus);
  return `${from} → ${to}`;
}

/**
 * Detects a stage transition that was fired automatically by a
 * server-side path (auto-followup engine, long-follow-up parking,
 * auto-lost-close, auto-warm promotion, auto-demote-stale cron etc.)
 * rather than by an operator clicking through the Status Changer.
 *
 * The signal is the `note` prefix: every auto-transition site in
 * `apps/api/src/modules/leads` writes a stage-history note starting
 * with `"Auto-"` ("Auto-flagged", "Auto-closed", "Auto-demoted",
 * "Auto-promoted", "Auto-parked"). The manual paths never do -
 * operator notes carry the operator's free-text or are null.
 *
 * The timeline uses this to render `System` as the actor instead of
 * whichever user id was persisted to `changed_by_id` (usually the
 * operator whose action triggered the side effect, or the seeded
 * admin user for cron writes). Backend attribution is unchanged;
 * only the display label swaps.
 */
export function isAutoStageTransition(entry: LeadStageHistoryEntry): boolean {
  return typeof entry.note === 'string' && entry.note.startsWith('Auto-');
}

/**
 * Display name for the actor of a stage-history row. Auto-transitions
 * render as `System` (see `isAutoStageTransition`) so ops can tell at
 * a glance which timeline entries were automatic side effects and
 * which were operator-driven.
 */
export function stageHistoryActorLabel(entry: LeadStageHistoryEntry): string {
  return isAutoStageTransition(entry) ? 'System' : entry.changedBy.fullName;
}

/**
 * Strips internal architecture-decision-record references from a note
 * before rendering. Historical rows in `lead_stage_history` still carry
 * text like `"Auto-parked: short-haul trip > 45 days out (ADR-0052)."`
 * from earlier auto-transition code paths, and the ops team doesn't
 * want internal ADR ids surfacing on the customer-facing lead timeline.
 * All new note strings on the server side avoid ADR refs (as of
 * 2026-07-27), so once the historical rows age out this becomes a no-op.
 * Left in place as defense-in-depth against any future note that
 * accidentally leaks a reference.
 *
 * Matches ` (ADR-<digits>)` and the same pattern with a trailing period
 * (`...(ADR-0052).` -> `....`) so the sanitized note stays clean.
 */
export function sanitizeStageHistoryNote(note: string | null | undefined): string | null {
  if (!note) return null;
  return note
    .replace(/\s*\(ADR-\d+(?:\s*[§#][^)]*)?\)/gi, '')
    .replace(/\s+\./g, '.')
    .trim();
}
